/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import type { PressableProps } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import IconRenderer from '../icon-renderer';
import { ThemedPressable } from './themed-pressable';
import type { CommonProps, IconButtonCommonProps, OverriddenPrimitiveProps } from './types';

const styles = cssMap({
	label: { fontWeight: token('font.weight.medium') },
});

export interface ThemedIconButtonProps
	extends CommonProps, Omit<PressableProps, OverriddenPrimitiveProps>, IconButtonCommonProps {
	/**
	 * Whether to suppress the icon button tooltip.
	 */
	isTooltipDisabled?: boolean;
	/**
	 * Show the label without replacing the underlying button. Used by ResponsiveTopNavButton.
	 */
	shouldShowLabel?: boolean;
}

export const ThemedIconButton: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<ThemedIconButtonProps> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, ThemedIconButtonProps>(function ThemedIconButton(
	{ icon: Icon, label, tooltip, isTooltipDisabled, shouldShowLabel = false, ...props },
	ref,
) {
	return (
		// Keep the trigger mounted when tooltip availability changes so focus is preserved.
		<Tooltip
			{...tooltip}
			content={isTooltipDisabled || shouldShowLabel ? null : (tooltip?.content ?? label)}
		>
			{(triggerProps) => (
				/**
				 * The `aria-describedby` from `triggerProps` is intentionally not passed down,
				 * because it would cause double announcements with the `VisuallyHidden` label.
				 *
				 * The `@atlaskit/button` IconButton uses the same approach.
				 */
				<ThemedPressable
					{...props}
					shape={shouldShowLabel ? 'default' : 'square'}
					ref={mergeRefs([ref, triggerProps.ref])}
					onClick={(event, analyticsEvent) => {
						props.onClick?.(event, analyticsEvent);
						triggerProps?.onClick?.(event);
					}}
					onMouseOver={(e) => {
						triggerProps.onMouseOver?.(e);
						props.onMouseOver?.(e);
					}}
					onMouseOut={(e) => {
						triggerProps.onMouseOut?.(e);
						props.onMouseOut?.(e);
					}}
					onMouseMove={(e) => {
						triggerProps.onMouseMove?.(e);
						props.onMouseMove?.(e);
					}}
					onMouseDown={(e) => {
						triggerProps.onMouseDown?.(e);
						props.onMouseDown?.(e);
					}}
					onFocus={(e) => {
						triggerProps.onFocus?.(e);
						props.onFocus?.(e);
					}}
					onBlur={(e) => {
						triggerProps.onBlur?.(e);
						props.onBlur?.(e);
					}}
				>
					<IconRenderer icon={Icon} />
					{shouldShowLabel ? (
						<span css={styles.label}>{label}</span>
					) : (
						<VisuallyHidden>{label}</VisuallyHidden>
					)}
				</ThemedPressable>
			)}
		</Tooltip>
	);
});
